<template>
<div>
    <reminder-form :getReminderList="getReminderList" ref="reminderFormModal"></reminder-form>
    <patent-form ref="patentFormModal"></patent-form>
    <national-patent-form ref="nationalPatentFormModal"></national-patent-form>
    <trademark-form ref="trademarkFormModal"></trademark-form>
    <trademark-opposition-form ref="trademarkOppositionFormModal"></trademark-opposition-form>
    <national-trademark-form ref="nationalTrademarkFormModal"></national-trademark-form>
    <national-trademark-search-form ref="nationalTrademarkSearchFormModal"></national-trademark-search-form>
    <trademark-search-form ref="trademarkSearchFormModal"></trademark-search-form>
    <design-form ref="designFormModal"></design-form>
    <design-opposition-form ref="designOppositionFormModal"></design-opposition-form>
    <domain-form ref="domainFormModal"></domain-form>
    <domain-opposition-form ref="domainOppositionFormModal"></domain-opposition-form>
    <invoice-form ref="invoiceFormModal"></invoice-form>
    <context-menu ref="copyMenu"></context-menu>
    <plant-variety-form ref="plantVarietyFormModal"></plant-variety-form>
    <div class="custom-search">
        <b-row>
            <b-col class="col-md-2">
                <label for="reminder-date-start-input">{{ $t('others.search') }}</label>
                <div class="d-flex align-items-center">
                    <b-form-input size="sm" v-model="searchTerm" :placeholder=" $t('others.search_name')" type="text" class="d-inline-block" />
                </div>
            </b-col>
            <b-col class="col-md-2">
                <label for="reminder-employee-input">{{ $t('reminderReport.employee') }}</label>
                <b-form-select id="reminder-employee-input" v-model="reminderEmployeeSelect" @change="reminderList()" v-on:keyup.enter="reminderList()" size="sm" value-field="id" text-field="name" :options="reminderEmployeeOption" />
            </b-col>
            <b-col class="col-md-2">
                <label for="reminder-date-start-input">{{ $t('reminderReport.reminder_to_date_start') }}</label>
                <div class="d-flex align-items-center">
                    <b-form-datepicker today-button :label-today-button="$t('dateTime.today')" :placeholder="$t('reminderReport.reminder_to_date_start')" size="sm" v-model="reminderToDateStart" class="mb1" defaultDate: null reset-button :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }" :locale="$i18n.locale" :label-reset-button="$t('dateTime.reset')" :label-no-date-selected="$t('dateTime.select_date')" />
                </div>
            </b-col>
            <b-col class="col-md-2">
                <label for="reminder-date-end-input">{{ $t('reminderReport.reminder_to_date_end') }}</label>
                <div class="d-flex align-items-center">
                    <b-form-datepicker today-button :label-today-button="$t('dateTime.today')" :placeholder="$t('reminderReport.reminder_to_date_end')" size="sm" v-model="reminderToDateEnd" class="mb1" defaultDate: null reset-button :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }" :locale="$i18n.locale" :label-reset-button="$t('dateTime.reset')" :label-no-date-selected="$t('dateTime.select_date')" />
                </div>
            </b-col>
            <b-col class="col-md-2">
                <b-form-checkbox v-model="showAllNextReminder" class="mt-1" switch inline>{{ $t('reminderReport.show_all_reminder') }}</b-form-checkbox>
            </b-col>
            <b-col class="col-md-2 mt-2 custom-search d-flex justify-content-end">
                <div class="d-flex align-items-center">
                    <b-button class="d-inline-block mr-05" size="sm" variant="outline-primary" v-on:keyup.enter="getReminderList()" @click="getReminderList()">{{ $t('reminderReport.list') }}</b-button>
                    <b-button class="d-inline-block mr-05" size="sm" variant="outline-primary" @click="getInfo(0,0,0)">{{ $t('reminderReport.add') }}</b-button>
                    <b-button class="d-inline-block" size="sm" variant="outline-primary" @click="exportExcel()">{{ $t('excel.excel') }}</b-button>
                </div>
            </b-col>
        </b-row>
    </div>
    <!-- table -->
    <vue-good-table ref="reminderTable" :columns="columns" :rows="rows" styleClass="vgt-table condensed tableSize" :search-options="{
          enabled: true,
          externalQuery: searchTerm}" :select-options="{
          enabled: false,
          selectOnCheckboxOnly: true, // only select when checkbox is clicked instead of the row
          selectionInfoClass: 'custom-class',
          selectionText: 'rows selected',
          clearSelectionText: 'clear',
          disableSelectInfo: true, // disable the select info panel on top
          selectAllByGroup: true, // when used in combination with a grouped table, add a checkbox in the header row to check/uncheck the entire group
        }" :pagination-options="{
          enabled: true,
          perPage:pageLength
        }" theme="my-theme">
        <div slot="emptystate">
            {{ $t('dataTable.data_not_found') }}
        </div>
        <!-- Row: Table Row -->
        <template slot="table-column" slot-scope="props">
            <span v-if="props.column.field ==='note'" class="text-nowrap">
                {{ $t('reminderReport.note') }}
            </span>
            <span v-else-if="props.column.field ==='recordDate'" class="text-nowrap">
                {{ $t('reminderReport.record_date') }}
            </span>
            <span v-else-if="props.column.field ==='reminderToDate'" class="text-nowrap">
                {{ $t('reminderReport.reminder_to_date') }}
            </span>
            <span v-else-if="props.column.field ==='employee'" class="text-nowrap">
                {{ $t('reminderReport.employee') }}
            </span>
            <span v-else-if="props.column.field ==='process'" class="text-nowrap">
                {{ $t('others.process') }}
            </span>
        </template>
        <!-- Column: Table Column -->
        <template slot="table-row" slot-scope="props">
            <!-- Column: Agent Name -->
            <span v-if="props.column.field === 'note'">
                <b-badge v-if="props.row.operationRecord.recordNo != null" @contextmenu.prevent="openContextMenu($event, props.row.note)" @click="showOperationForm(props.row)" variant="primary" style="cursor: pointer;">
                    <feather-icon icon="MousePointerIcon" class="mr-25" />
                    <span>{{ props.row.operationRecord.recordNo }}</span>
                </b-badge>
                {{ props.row.note.replace(props.row.operationRecord.recordNo, '') }}
            </span>
            <span v-else-if="props.column.field === 'process'">
                <span>
                    <b-button v-b-tooltip.html :title=" $t('others.edit')" v-b-modal.modal-top variant="primary" class="btn-icon mb-1 ml-1" size="sm" @click="getInfo(props.row.id, props.row.recordId, props.row.moduleId)">
                        <feather-icon icon="EditIcon" />
                    </b-button>
                    <b-button v-b-tooltip.html :title=" $t('others.delete')" variant="warning" class="btn-icon mb-1 ml-1" size="sm" @click="deleted(props.row)">
                        <feather-icon icon="XIcon" />
                    </b-button>
                </span>
            </span>
        </template>
        <!-- pagination -->
        <template slot="pagination-bottom" slot-scope="props">
            <div class="d-flex justify-content-between flex-wrap">
                <div class="d-flex align-items-center mb-0 mt-1">
                    <span class="text-nowrap"> {{ $t('dataTable.total') }} {{ props.total }} {{ $t('dataTable.record') }} </span>
                    <b-form-select v-model="pageLength" :options="['10','50','100', '1000']" class="mx-1" @input="(value)=>props.perPageChanged({currentPerPage:value})" />
                    <span class="text-nowrap"> {{ $t('dataTable.row_is_show') }} </span>
                </div>
                <div>
                    <b-pagination :value="1" :total-rows="props.total" :per-page="pageLength" first-number last-number align="right" prev-class="prev-item" next-class="next-item" class="mt-1 mb-0" @input="(value)=>props.pageChanged({currentPage:value})">
                        <template #prev-text>
                            <feather-icon icon="ChevronLeftIcon" size="18" />
                        </template>
                        <template #next-text>
                            <feather-icon icon="ChevronRightIcon" size="18" />
                        </template>
                    </b-pagination>
                </div>
            </div>
        </template>
    </vue-good-table>
</div>
</template>

<script>
export default {
    data() {
        return {
            pageLength: 10,
            dir: false,
            columns: [{
                    label: this.$t('reminderReport.note'),
                    field: `note`,
                },
                {
                    label: this.$t('reminderReport.record_date'),
                    field: `recordDate`,
                    formatFn: this.$formatFn,
                },
                {
                    label: this.$t('reminderReport.reminder_date'),
                    field: `reminderToDate`,
                    formatFn: this.$formatFn,
                },
                {
                    label: this.$t('reminderReport.employee'),
                    field: `employee`,
                },
                {
                    label: 'Process',
                    field: 'process',
                    width: '10em'
                },
            ],
            rows: [],
            searchTerm: '',
            reminderToDateStart: null,
            showAllNextReminder: false,
            reminderToDateEnd: new Date(),
            reminderEmployeeOption: [],
            reminderEmployeeSelect: 0
        }
    },
    watch: {
        reminderToDateStart(newValue) {
            if (newValue == '') {
                this.reminderToDateStart = null
            }
        },
        reminderToDateEnd(newValue) {
            if (newValue == '') {
                this.reminderToDateEnd = null
            }
        },
    },
    mounted() {
        this.getReminderList()
    },
    methods: {
        openContextMenu(event, row) {
            this.$refs.copyMenu.openContextMenu(event, row)
        },
        showOperationForm(row) {
            if (row.moduleId == 2) {
                this.$refs.trademarkFormModal.showInfo(row.recordId)
            } else if (row.moduleId == 3) {
                this.$refs.trademarkSearchFormModal.showInfo(row.recordId)
            } else if (row.moduleId == 4) {
                this.$refs.patentFormModal.showInfo(row.recordId)
            } else if (row.moduleId == 5) {
                this.$refs.designFormModal.showInfo(row.recordId)
            } else if (row.moduleId == 6) {
                this.$refs.trademarkOppositionFormModal.showInfo(row.recordId)
            } else if (row.moduleId == 7) {
                this.$refs.domainFormModal.showInfo(row.recordId)
            } else if (row.moduleId == 8) {
                this.$refs.domainOppositionFormModal.showInfo(row.recordId)
            } else if (row.moduleId == 9) {
                this.$refs.designOppositionFormModal.showInfo(row.recordId)
            } else if (row.moduleId == 13) {
                this.$refs.plantVarietyFormModal.showInfo(row.recordId)
            } else if (row.moduleId == 15) {
                this.$refs.nationalTrademarkFormModal.showInfo(row.recordId)
            } else if (row.moduleId == 16) {
                this.$refs.nationalPatentFormModal.showInfo(row.recordId)
            } else if (row.moduleId == 20) {
                this.$refs.invoiceFormModal.showInfo(row.recordId)
            } else if (row.moduleId == 23) {
                this.$refs.nationalTrademarkSearchFormModal.showInfo(row.recordId)
            }
        },
        async deleted(data) {
            data = {
                id: data.id,
                employeeId: localStorage.getItem('EmployeeId')
            }
            const controller = {
                name: 'Reminder'
            }
            const payload = {
                data: data,
                controller: controller
            }
            this.$DeleteConfirm().then(result => {
                if (result.value) {
                    this.$store.dispatch('moduleAuth/delete', payload).then((response) => {
                        if (response.data.resultStatus == true) {
                            this.$DeleteAlert()
                            this.getReminderList()
                        } else {
                            this.$CustomWarningAlert(response.data.resultMessage)
                            this.getReminderList()
                        }
                    })
                }
            })
        },
        async getInfo(id, recordId, moduleId) {
            this.$refs.reminderFormModal.showInfo(id, recordId, moduleId)
        },
        getReminderList() {
            this.rows = []
            const data = {
                employeeId: localStorage.getItem("EmployeeId"),
                reminderToDateStart: this.reminderToDateStart,
                reminderToDateEnd: this.reminderToDateEnd,
                showAllNextReminder: this.showAllNextReminder,
                employeeIdToOnlyList: this.reminderEmployeeSelect
            }
            const controller = {
                name: 'Reminder',
                actionName: "GetReminderReportList"
            }
            const payload = {
                data: data,
                controller: controller
            }
            this.$store.dispatch('moduleAuth/customService', payload).then((response) => {
                if (response.data.resultStatus == true) {
                    this.reminderEmployeeOption = response.data.employeeList
                    this.reminderEmployeeOption.splice(0, 0, this.$nullSelected())
                    response.data.reminderList.forEach(element => {
                        this.rows.push(element)
                    })
                } else {
                    this.$WarningAlert(response.data.resultMessage)
                }
            })
        },
        exportExcel() {
            const controller = {
                name: 'Reminder',
                actionName: 'CanExport'
            }
            const data = {
                columnList: this.columns,
                DataList: this.rows
            }
            const payload = {
                data: data,
                controller: controller
            }
            this.$store.dispatch('moduleAuth/customService', payload).then((response) => {
                if (response.data.resultStatus == true) {
                    this.$downloadFile("Hatırlatmalar", response.data.base64, 'xlsx')
                } else {
                    this.$WarningAlert(response.data.resultMessage)
                }
            })
        }
    },
}
</script>

<style lang="scss">
.excel-css {
    float: right;
    margin-right: 1em;
    width: 9.5em;
    color: #7367f0;
    border: 1px solid #7367f0;
    background-color: transparent;
    margin-left: 1em;
    height: 2em;
    border-radius: 0.358rem;
}

.tableSize {
    font-size: 13px !important;
    border-collapse: collapse;
    width: 100%;
    max-width: 100%;
    table-layout: auto;
}
</style>
